import React, { FC, memo, useCallback, useRef } from 'react'
import { useGetCurrencyIcon } from 'astra-core/hooks/useGetCurrenciesIcon'
import { useTranslation } from 'react-i18next'
import { selectCurrencyMinBet } from 'astra-core/containers/CommonDataProvider'
import { useQuickBetForm } from 'og-web-core/providers/basket/quick-bet/hooks/useQuickBetForm'
import { formatString } from 'og-web-core/providers/basket/quick-bet/basket-quick-bet.utils'
import {
  EFeatureFlags,
  useFeatureFlag
} from 'astra-core/containers/ConfigProvider'
import {
  betsProviderActions,
  selectIsQuickBetActive
} from 'astra-core/containers/BetsProvider'
import { ogBetProviderActions } from 'og-web-core/providers/bet/slice'
import { setLocalStorageItem } from 'og-web-core/utils/storage/local-storage'
import { StorageKeys } from 'og-web-core/utils/storage/constants'
import { selectQuickBetModal } from 'og-web-core/providers/bet/selectors'
import { useUpdateQuickBet } from 'astra-core/containers/SettingsProvider/useUpdateQuickBet'

import { ETestData } from 'shared/lib/testData'
import { CurrencyDynamicInput } from 'shared/ui/DynamicInput'
import { useAppSelector, useAppDispatch } from 'store'

import { BasketQuickBetError } from '../BasketQuickBetError/BasketQuickBetError'
import { EBasketQuickBetError } from '../BasketQuickBetError/BasketQuickBetError.types'
import { BasketQuickBetSubmitButton } from '../BasketQuickBetSubmitButton/BasketQuickBetSubmitButton'
import {
  StyledBetCurrency,
  StyledBetInputWrapper
} from '../../../BasketCommon/components/BasketInput/BasketInput.styled'
import { BasketQuickAmounts } from '../../../BasketQuickAmounts/BasketQuickAmounts'

import {
  StyledQuickBetForm,
  StyledQuickBetInput,
  StyledQuickBetInputDescription,
  StyledQuickBetInputError,
  StyledQuickBetInputLabel,
  StyledQuickBetInputWrapper
} from './BasketQuickBetForm.styled'
import { BasketQuickBetFormProps } from './BasketQuickBetForm.types'

const errorsKeys = {
  [EBasketQuickBetError.minBetError]: 'minimum amount'
}
export const BasketQuickBetForm: FC<BasketQuickBetFormProps> = memo(() => {
  const minBet = useAppSelector(selectCurrencyMinBet)
  const inputRef = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()

  const focusOnInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])
  const isQuickBetActive = useAppSelector(selectIsQuickBetActive)
  const modal = useAppSelector(selectQuickBetModal)

  const { inputValue, handleChangeInput, handleSelectAmount, error } =
    useQuickBetForm()

  const currencyIcon = useGetCurrencyIcon()
  const { t } = useTranslation()
  const isBetsAmountEnabled = useFeatureFlag(EFeatureFlags.BETS_AMOUNT_ENABLED)

  const onSuccess = useCallback(() => {
    if (modal) {
      dispatch(ogBetProviderActions.setQuickBetModal(null))
    }
    dispatch(betsProviderActions.setQuickBetActive({ isActive: true }))
    setLocalStorageItem(StorageKeys.isQuickBetActive, true)
  }, [dispatch, modal])

  const { isLoading, updateQuickBetSum } = useUpdateQuickBet({
    onError: (err) => {
      console.error(err)
    },
    onSuccess
  })

  const handleSubmit = () => {
    const quickBetValue = formatString(inputValue)
    updateQuickBetSum(quickBetValue)
  }

  return (
    <>
      <StyledQuickBetForm>
        <StyledQuickBetInputWrapper>
          <StyledQuickBetInputLabel>
            {t('bets amount')}
          </StyledQuickBetInputLabel>
          <StyledQuickBetInput
            data-test-id={ETestData.TestQuickBetInput}
            error={error}
            onClick={focusOnInput}
          >
            <StyledBetInputWrapper>
              <CurrencyDynamicInput
                inputValue={inputValue}
                placeholder="0"
                ref={inputRef}
                onAmoutChange={handleChangeInput}
              />
              <StyledBetCurrency>{currencyIcon}</StyledBetCurrency>
            </StyledBetInputWrapper>
          </StyledQuickBetInput>
          {error ? (
            <StyledQuickBetInputError
              data-test-id={ETestData.TestQuickBetInputValidationText}
            >
              {t(errorsKeys[error], {
                amount: minBet,
                currency: currencyIcon
              })}
            </StyledQuickBetInputError>
          ) : (
            <StyledQuickBetInputDescription>
              {t('minimum bet amount', {
                amount: minBet,
                currency: currencyIcon
              })}
            </StyledQuickBetInputDescription>
          )}
        </StyledQuickBetInputWrapper>

        {isBetsAmountEnabled && (
          <BasketQuickAmounts
            stakeAmount={+inputValue}
            onChange={handleSelectAmount}
          />
        )}
        {!!error && <BasketQuickBetError error={error} />}
      </StyledQuickBetForm>
      <BasketQuickBetSubmitButton
        disabled={!!error}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        testData={ETestData.TestQuickBetSaveSum}
        title="save"
      />
    </>
  )
})
